import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash, ArrowsLeftRight, ArrowsClockwise, Warning } from '@phosphor-icons/react';

export default function AdminHub() {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const handleSync = async () => {
    if (!window.confirm('This will sync all data from Supabase, recalculate all financial balances, and rebuild transactions. Continue?')) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      await api.post('/admin/supabase-sync');
      toast.info('Sync started in background. Checking progress...');
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const { data: status } = await api.get('/admin/sync-status');
          if (!status.running) {
            clearInterval(poll);
            setSyncing(false);
            setSyncResult(status.last_result);
            if (status.last_result?.status === 'success') {
              toast.success('Supabase sync completed successfully');
            } else if (status.last_result?.status === 'error') {
              toast.error(`Sync error: ${status.last_result.error}`);
            }
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Sync failed to start');
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-hub">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900 font-['Chivo']">Administration</h2>
        <Button onClick={handleSync} disabled={syncing}
          className="bg-[#107C41] hover:bg-[#0B5A2F] text-white rounded-sm text-sm" data-testid="supabase-sync-btn">
          <ArrowsClockwise size={16} className={`mr-1 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync from Supabase'}
        </Button>
      </div>

      {syncResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
          <p className="text-sm font-medium text-emerald-800 mb-2">Sync Complete</p>
          <ul className="space-y-1">
            {syncResult.log?.map((line, i) => (
              <li key={i} className="text-xs text-emerald-700">{line}</li>
            ))}
          </ul>
        </div>
      )}

      <Tabs defaultValue="schools">
        <TabsList className="flex border-b border-slate-200 bg-transparent p-0 h-auto">
          {['Schools', 'Categories', 'Fund Transfers', 'Audit Log'].map(tab => (
            <TabsTrigger key={tab} value={tab.toLowerCase().replace(/\s+/g, '-')}
              className="px-4 py-2 text-sm font-medium text-slate-500 border-b-2 border-transparent rounded-none data-[state=active]:text-[#0F6CBD] data-[state=active]:border-[#0F6CBD] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              data-testid={`admin-tab-${tab.toLowerCase().replace(/\s+/g, '-')}`}>{tab}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="schools" className="mt-4"><SchoolsTab /></TabsContent>
        <TabsContent value="categories" className="mt-4"><CategoriesTab /></TabsContent>
        <TabsContent value="fund-transfers" className="mt-4"><FundTransfersTab /></TabsContent>
        <TabsContent value="audit-log" className="mt-4"><AuditLogTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function SchoolsTab() {
  const [schools, setSchools] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', contact: '', email: '' });
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try { const { data } = await api.get('/admin/schools'); setSchools(data); } catch {}
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/schools', form);
      toast.success('School created'); setShowForm(false); setForm({ name: '', address: '', contact: '', email: '' }); fetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="schools-tab">
      <div className="flex justify-between">
        <span className="text-sm font-semibold text-slate-700">Schools ({schools.length})</span>
        <Button size="sm" onClick={() => setShowForm(true)} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm text-xs" data-testid="create-school-btn">
          <Plus size={14} className="mr-1" /> Add School
        </Button>
      </div>
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="data-label">Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-sm" data-testid="school-name-input" /></div>
            <div><Label className="data-label">Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="rounded-sm" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="data-label">Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="rounded-sm" /></div>
            <div><Label className="data-label">Contact</Label><Input value={form.contact} onChange={e => setForm({...form, contact: e.target.value})} className="rounded-sm" /></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm text-xs" data-testid="school-save-btn">{saving ? 'Saving...' : 'Save'}</Button>
            <Button variant="ghost" onClick={() => setShowForm(false)} className="text-xs">Cancel</Button>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead><tr className="bg-slate-50 border-b border-slate-200">
            {['Name', 'Address', 'Contact', 'Email', 'Actions'].map(h => <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-3 px-4">{h}</th>)}
          </tr></thead>
          <tbody>
            {schools.length === 0 ? <tr><td colSpan={5} className="py-4 text-center text-sm text-slate-400">No schools</td></tr> :
              schools.map(s => (
                <tr key={s.id} className="border-b border-slate-100">
                  <td className="py-3 px-4 text-sm font-medium text-slate-800">{s.name}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{s.address}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{s.contact}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{s.email}</td>
                  <td className="py-3 px-4">
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={async () => {
                      await api.delete(`/admin/schools/${s.id}`); toast.success('Deleted'); fetch();
                    }} data-testid={`delete-school-${s.id}`}><Trash size={14} /></Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'recipient', subtype: '' });
  const [filterType, setFilterType] = useState('');
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const params = filterType ? `?type=${filterType}` : '';
      const { data } = await api.get(`/admin/categories${params}`);
      setCategories(data);
    } catch {}
  }, [filterType]);
  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    if (!form.name || !form.type) { toast.error('Name and type required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/categories', form);
      toast.success('Category created'); setShowForm(false); setForm({ name: '', type: 'recipient', subtype: '' }); fetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="categories-tab">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-slate-700">Categories ({categories.length})</span>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-36 rounded-sm"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="recipient">Recipient</SelectItem><SelectItem value="expense">Expense</SelectItem><SelectItem value="donor">Donor</SelectItem></SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={() => setShowForm(true)} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm text-xs" data-testid="create-category-btn">
          <Plus size={14} className="mr-1" /> Add Category
        </Button>
      </div>
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="data-label">Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-sm" data-testid="category-name-input" /></div>
            <div><Label className="data-label">Type *</Label>
              <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                <SelectTrigger className="rounded-sm" data-testid="category-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="recipient">Recipient</SelectItem><SelectItem value="expense">Expense</SelectItem><SelectItem value="donor">Donor</SelectItem></SelectContent>
              </Select></div>
            <div><Label className="data-label">Subtype</Label><Input value={form.subtype} onChange={e => setForm({...form, subtype: e.target.value})} className="rounded-sm" /></div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm text-xs" data-testid="category-save-btn">{saving ? 'Saving...' : 'Save'}</Button>
            <Button variant="ghost" onClick={() => setShowForm(false)} className="text-xs">Cancel</Button>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead><tr className="bg-slate-50 border-b border-slate-200">
            {['Name', 'Type', 'Subtype', 'Actions'].map(h => <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-3 px-4">{h}</th>)}
          </tr></thead>
          <tbody>
            {categories.length === 0 ? <tr><td colSpan={4} className="py-4 text-center text-sm text-slate-400">No categories</td></tr> :
              categories.map(c => (
                <tr key={c.id} className="border-b border-slate-100">
                  <td className="py-3 px-4 text-sm font-medium text-slate-800">{c.name}</td>
                  <td className="py-3 px-4"><Badge variant="outline" className="capitalize text-xs">{c.type}</Badge></td>
                  <td className="py-3 px-4 text-sm text-slate-600">{c.subtype}</td>
                  <td className="py-3 px-4">
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={async () => {
                      await api.delete(`/admin/categories/${c.id}`); toast.success('Deleted'); fetch();
                    }} data-testid={`delete-cat-${c.id}`}><Trash size={14} /></Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FundTransfersTab() {
  const [form, setForm] = useState({ from_student_id: '', to_student_id: '', amount: '', description: '' });
  const [transfers, setTransfers] = useState([]);
  const [students, setStudents] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/recipients?type=student&status=active').then(r => setStudents(r.data)).catch(() => {});
    api.get('/admin/fund-transfers').then(r => setTransfers(r.data)).catch(() => {});
  }, []);

  const handleTransfer = async () => {
    if (!form.from_student_id || !form.to_student_id || !form.amount) { toast.error('All fields required'); return; }
    if (form.from_student_id === form.to_student_id) { toast.error('Cannot transfer to same student'); return; }
    setSaving(true);
    try {
      await api.post('/admin/fund-transfer', { ...form, amount: parseFloat(form.amount) });
      toast.success('Funds transferred');
      setForm({ from_student_id: '', to_student_id: '', amount: '', description: '' });
      const { data } = await api.get('/admin/fund-transfers');
      setTransfers(data);
      const s = await api.get('/recipients?type=student&status=active');
      setStudents(s.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="fund-transfers-tab">
      <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-4">
        <h3 className="text-sm font-semibold text-slate-700 font-['Chivo'] flex items-center gap-2"><ArrowsLeftRight size={16} /> Transfer Funds Between Students</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="data-label">From Student *</Label>
            <Select value={form.from_student_id} onValueChange={v => setForm({...form, from_student_id: v})}>
              <SelectTrigger className="rounded-sm" data-testid="transfer-from-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name} (${(s.wallet_balance || 0).toFixed(2)})</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="data-label">To Student *</Label>
            <Select value={form.to_student_id} onValueChange={v => setForm({...form, to_student_id: v})}>
              <SelectTrigger className="rounded-sm" data-testid="transfer-to-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="data-label">Amount *</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="rounded-sm" data-testid="transfer-amount-input" /></div>
          <div><Label className="data-label">Description</Label>
            <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-sm" /></div>
        </div>
        <Button onClick={handleTransfer} disabled={saving} className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm text-sm" data-testid="transfer-btn">
          {saving ? 'Transferring...' : 'Transfer Funds'}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="p-3 border-b border-slate-200"><span className="text-sm font-semibold text-slate-700">Transfer History</span></div>
        <table className="w-full">
          <thead><tr className="bg-slate-50 border-b border-slate-200">
            {['Date', 'Amount', 'From', 'To', 'Reference'].map(h => <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-3 px-4">{h}</th>)}
          </tr></thead>
          <tbody>
            {transfers.length === 0 ? <tr><td colSpan={5} className="py-4 text-center text-sm text-slate-400">No transfers</td></tr> :
              transfers.map(t => (
                <tr key={t.id} className="border-b border-slate-100">
                  <td className="py-3 px-4 text-sm text-slate-600">{t.created_at?.substring(0, 10)}</td>
                  <td className="py-3 px-4 currency text-sm">${(t.amount || 0).toFixed(2)}</td>
                  <td className="py-3 px-4 text-sm text-slate-700">{t.from_entity}</td>
                  <td className="py-3 px-4 text-sm text-slate-700">{t.to_entity}</td>
                  <td className="py-3 px-4 text-xs text-slate-500">{t.reference}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [filterEntity, setFilterEntity] = useState('');

  const fetch = useCallback(async () => {
    try {
      const params = filterEntity ? `?entity_type=${filterEntity}` : '';
      const { data } = await api.get(`/admin/audit-log${params}`);
      setLogs(data);
    } catch {}
  }, [filterEntity]);
  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-slate-700">Audit Log ({logs.length})</span>
        <Select value={filterEntity} onValueChange={setFilterEntity}>
          <SelectTrigger className="w-40 rounded-sm"><SelectValue placeholder="All Entities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {['user', 'donor', 'donation', 'recipient', 'funding_request', 'expense', 'document', 'school', 'category', 'fund', 'report'].map(e =>
              <SelectItem key={e} value={e}>{e.replace(/_/g, ' ')}</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden max-h-[600px] overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0"><tr className="bg-slate-50 border-b border-slate-200">
            {['Timestamp', 'Action', 'Entity', 'Details'].map(h => <th key={h} className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider py-3 px-4">{h}</th>)}
          </tr></thead>
          <tbody>
            {logs.length === 0 ? <tr><td colSpan={4} className="py-4 text-center text-sm text-slate-400">No log entries</td></tr> :
              logs.map(l => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-4 text-xs text-slate-500 whitespace-nowrap">{l.timestamp?.replace('T', ' ').substring(0, 19)}</td>
                  <td className="py-2 px-4"><Badge variant="outline" className="text-xs">{l.action?.replace(/_/g, ' ')}</Badge></td>
                  <td className="py-2 px-4 text-xs text-slate-600 capitalize">{l.entity_type}</td>
                  <td className="py-2 px-4 text-xs text-slate-600 max-w-md truncate">{l.details}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
